/**
 * Pure logic behind the deliveries board. Kept out of the
 * React component so the grouping is testable without a DOM.
 *
 * <p><b>Deliberately no drag and drop.</b> Every transition in this workflow
 * carries data — the weighbridge figures, the analysis, the settled amount — so
 * a card cannot be dragged into the next column without somebody typing
 * something first. The board is a view; the actions stay on the card and on the
 * detail page, where the forms are.
 */
import type { Delivery, DeliveryStatus } from "../api/deliveries";

/**
 * The delivery lifecycle in column order. ANNOUNCED→SETTLED is the happy path
 * and is always shown; REJECTED is a terminal side-exit, shown only when it
 * actually holds something, so a clean week is not cluttered by an empty column.
 */
export const BOARD_STATUS_ORDER: DeliveryStatus[] = [
  "ANNOUNCED",
  "CONFIRMED",
  "SAMPLED",
  "RECEIVED",
  "SETTLED",
  "LAB_REJECTED",
  "REJECTED",
  "CANCELLED",
];

/**
 * The columns always shown. LAB_REJECTED is a side-exit like REJECTED and appears
 * only when it holds something — but it is the column somebody has to act on, so it
 * sits before REJECTED rather than after it.
 */
const CORE_STATUSES: ReadonlySet<DeliveryStatus> = new Set<DeliveryStatus>([
  "ANNOUNCED",
  "CONFIRMED",
  "SAMPLED",
  "RECEIVED",
  "SETTLED",
]);

export type DeliveryColumn = {
  status: DeliveryStatus;
  deliveries: Delivery[];
  count: number;
  /** Announced tonnage before weighing, measured net after — what the column actually represents. */
  sumTonnes: number;
};

/** Net kilograms once weighed, otherwise the supplier's estimate in tonnes. */
export function tonnesOf(d: Delivery): number {
  if (d.netKg != null) return d.netKg / 1000;
  return d.estimatedQuantityT ?? 0;
}

export function groupDeliveriesByStatus(deliveries: Delivery[]): DeliveryColumn[] {
  const byStatus = new Map<DeliveryStatus, Delivery[]>();
  for (const d of deliveries) {
    const bucket = byStatus.get(d.status);
    if (bucket) bucket.push(d);
    else byStatus.set(d.status, [d]);
  }
  return BOARD_STATUS_ORDER.filter(
    (s) => CORE_STATUSES.has(s) || (byStatus.get(s)?.length ?? 0) > 0,
  ).map((status) => {
    const list = byStatus.get(status) ?? [];
    return {
      status,
      deliveries: list,
      count: list.length,
      sumTonnes: list.reduce((sum, d) => sum + tonnesOf(d), 0),
    };
  });
}
